/**
 * Razorpay Standard Checkout integration for Pivotbox.
 *
 * Flow:
 *  1. Caller clicks "Pay & Unlock Full Diagnostic" (renders <RazorpayCheckoutButton/>)
 *  2. We POST /api/payments/create-order → get { order_id, key_id, amount, currency }
 *  3. Dynamically load checkout.razorpay.com/v1/checkout.js (cached after first load)
 *  4. Open the Razorpay modal with the order_id
 *  5. On success: POST /api/payments/verify with { order_id, payment_id, signature }
 *  6. Backend HMAC-verifies, mints a single-use access token, returns redirect_to
 *  7. We navigate to /diagnostic?token=<token>
 *
 * KEY_SECRET never reaches the browser; key_id is public (intentional).
 */
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { trackLead, trackInitiateCheckout, trackPurchase } from "@/components/pivotbox/metaPixel";
import { gaBeginCheckout, gaPurchase } from "@/components/pivotbox/gaEvents";

const CHECKOUT_SCRIPT = "https://checkout.razorpay.com/v1/checkout.js";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.querySelector(`script[src="${CHECKOUT_SCRIPT}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(true));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.src = CHECKOUT_SCRIPT;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function RazorpayCheckoutButton({
  amount = 399900,                 // 3,999 INR in paise (override per-CTA if needed)
  currency = "INR",
  product = "full_diagnostic",
  buttonLabel = "Pay & Unlock Full Diagnostic",
  className = "rzp-checkout-btn",
  prefill = {},                    // { name, email, contact }
  notes = {},
  onSuccess,                       // optional: ({ access_token, payment_id }) => void
  testId = "razorpay-checkout-btn",
  requireDetails = true,           // collect name/email/mobile BEFORE Razorpay opens
  ctaSubtitle = "Sends your magic link to your inbox.",
}) {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [details, setDetails] = useState({
    name: prefill.name || "",
    email: prefill.email || "",
    mobile: prefill.contact || prefill.mobile || "",
  });
  const [formError, setFormError] = useState("");
  const navigate = useNavigate();

  const startCheckout = useCallback(async (collected) => {
    if (loading) return;
    setLoading(true);
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const buyer = {
      name: (collected && collected.name) || prefill.name || "",
      email: (collected && collected.email) || prefill.email || "",
      mobile: (collected && collected.mobile) || prefill.contact || prefill.mobile || "",
    };
    // Meta Pixel: Lead — the user just submitted name+email+mobile
    trackLead({ value: amount / 100, currency, contentName: product });
    try {
      const scriptOk = await loadRazorpayScript();
      if (!scriptOk) {
        toast.error("Could not reach Razorpay. Please check your connection and retry.");
        setLoading(false);
        return;
      }

      // 1. Create order on the backend (with the captured buyer details)
      const orderRes = await fetch(`${API_URL}/api/payments/create-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount,
          currency,
          product,
          name: buyer.name,
          email: buyer.email,
          mobile: buyer.mobile,
        }),
      });
      if (!orderRes.ok) {
        const err = await orderRes.json().catch(() => ({}));
        toast.error(err.detail || "Could not start checkout. Please try again.");
        setLoading(false);
        return;
      }
      const order = await orderRes.json();

      // 2. Open Razorpay modal
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id,
        name: "Pivotbox",
        description: "Full Founder Diagnostic — S1 to S4",
        image: "/pivotbox-p-logo.png",
        prefill: {
          name: buyer.name,
          email: buyer.email,
          contact: buyer.mobile,
        },
        notes,
        theme: { color: "#2F7D57" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast("Payment cancelled. Your seat is still available — try again whenever you're ready.");
          },
        },
        handler: async (resp) => {
          // 3. Verify on backend
          try {
            const verifyRes = await fetch(`${API_URL}/api/payments/verify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
            });
            if (!verifyRes.ok) {
              const err = await verifyRes.json().catch(() => ({}));
              toast.error(err.detail || "Payment received but verification failed. We'll reach out — please don't pay again.");
              setLoading(false);
              return;
            }
            const result = await verifyRes.json();
            toast.success("Payment confirmed! Redirecting to your diagnostic…");

            // Fire-and-forget: write name/mobile/email to Mongo + Google Sheets
            // immediately, so the lead is captured even if the user never clicks
            // "Let's start" on /diagnostic. Same row gets upserted later when
            // they actually begin the assessment.
            try {
              const sessionId = `pay_${(result.access_token || resp.razorpay_payment_id || "").slice(0, 24)}`;
              fetch(`${API_URL}/api/diagnostic/start`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  session_id: sessionId,
                  mobile: (buyer.mobile || "").replace(/\D/g, "") || "0000000000",
                  name: buyer.name || "",
                  email: buyer.email || "",
                  source: amount <= 100 ? "test_pay_rs1" : "razorpay_paid",
                  access_token: result.access_token || "",
                }),
              }).catch(() => { /* silent — analytics-style fire-and-forget */ });
            } catch (e) { /* ignore */ }

            // Meta Pixel: Purchase — actual money received & HMAC verified
            trackPurchase({
              value: amount / 100,
              currency,
              transactionId: result.payment_id || resp.razorpay_payment_id,
              contentName: product,
            });
            // GA4: purchase
            gaPurchase({
              value: amount / 100,
              currency,
              transactionId: result.payment_id || resp.razorpay_payment_id,
            });
            if (onSuccess) onSuccess(result);
            // Hand off to /diagnostic with the unlock token
            navigate(result.redirect_to || `/diagnostic?token=${result.access_token}`);
          } catch (e) {
            console.error(e);
            toast.error("Network error verifying payment. We'll contact you — please don't pay again.");
            setLoading(false);
          }
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (err) => {
        console.error("Razorpay payment.failed:", err);
        toast.error(err?.error?.description || "Payment failed. Please try again.");
        setLoading(false);
      });
      // Meta Pixel: InitiateCheckout — Razorpay modal is opening
      trackInitiateCheckout({ value: amount / 100, currency, contentName: product });
      // GA4: begin_checkout
      gaBeginCheckout({ value: amount / 100, currency });
      rzp.open();
    } catch (e) {
      console.error("Razorpay checkout error:", e);
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  }, [amount, currency, product, prefill, notes, navigate, onSuccess, loading]);

  const handleButtonClick = useCallback(() => {
    if (loading) return;
    if (requireDetails) {
      setFormError("");
      setDialogOpen(true);
      return;
    }
    startCheckout(details);
  }, [loading, requireDetails, details, startCheckout]);

  const handleDialogSubmit = useCallback((e) => {
    e.preventDefault();
    const name = (details.name || "").trim();
    const email = (details.email || "").trim();
    const mobile = (details.mobile || "").replace(/\D/g, "");
    if (name.length < 2) { setFormError("Please enter your full name."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFormError("Please enter a valid email so we can send your magic link."); return; }
    if (mobile.length < 10) { setFormError("Please enter a valid 10-digit mobile number."); return; }
    setFormError("");
    setDialogOpen(false);
    startCheckout({ name, email, mobile });
  }, [details, startCheckout]);

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handleButtonClick}
        disabled={loading}
        data-testid={testId}
      >
        {loading ? "Opening checkout…" : buttonLabel}
      </button>

      {dialogOpen && (
        <div className="rzp-dialog-backdrop" data-testid={`${testId}-dialog`} onClick={() => !loading && setDialogOpen(false)}>
          <form
            className="rzp-dialog"
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleDialogSubmit}
            data-testid={`${testId}-dialog-form`}
          >
            <button
              type="button"
              className="rzp-dialog__close"
              onClick={() => setDialogOpen(false)}
              aria-label="Close"
              data-testid={`${testId}-dialog-close`}
            >&times;</button>
            <p className="rzp-dialog__eyebrow">Almost there</p>
            <h3 className="rzp-dialog__title">Your details</h3>
            <p className="rzp-dialog__sub">{ctaSubtitle}</p>

            <label className="rzp-dialog__label">
              <span>Full name</span>
              <input
                type="text"
                value={details.name}
                onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
                placeholder="Your full name"
                required
                data-testid={`${testId}-dialog-name`}
              />
            </label>
            <label className="rzp-dialog__label">
              <span>Email</span>
              <input
                type="email"
                value={details.email}
                onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))}
                placeholder="you@email.com"
                required
                data-testid={`${testId}-dialog-email`}
              />
            </label>
            <label className="rzp-dialog__label">
              <span>Mobile</span>
              <input
                type="tel"
                value={details.mobile}
                onChange={(e) => setDetails((d) => ({ ...d, mobile: e.target.value }))}
                placeholder="10-digit mobile"
                inputMode="numeric"
                maxLength={10}
                required
                data-testid={`${testId}-dialog-mobile`}
              />
            </label>

            {formError && <p className="rzp-dialog__error" role="alert">{formError}</p>}

            <button
              type="submit"
              className="rzp-dialog__cta"
              disabled={loading}
              data-testid={`${testId}-dialog-submit`}
            >
              Continue to payment &nbsp;&rarr;
            </button>
            <p className="rzp-dialog__legal">
              By continuing you agree to our <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a> and
              {" "}<a href="/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </form>
        </div>
      )}
    </>
  );
}
